<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { extraFieldTypeEnum, usePortalStore } from '@/stores/portal.store'
import { useDetectDevice } from '@/composables/device'
import { newExtraField } from '@/composables/extra-fields'

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const i18n = getI18n()
const portalStore = usePortalStore()
const { isMobile } = useDetectDevice()

const fieldTypes = computed(() => portalStore.getLocalizedExtraFieldTypes())
const fieldTypeOf = (field) => fieldTypes.value.find(type => type.id === field.field_type)

const update = (fields) => emit('update:modelValue', fields)
const updateField = (index, changes) => update(props.modelValue.map((field, i) => (i === index ? { ...field, ...changes } : field)))
const removeField = (index) => update(props.modelValue.filter((_field, i) => i !== index))
const moveField = (index, offset) => {
  const fields = [...props.modelValue]
  fields.splice(index + offset, 0, ...fields.splice(index, 1))
  update(fields)
}
const onTypeChange = (index, fieldType) => updateField(index, { field_type: fieldType, options: fieldType === extraFieldTypeEnum.SELECT ? props.modelValue[index].options : [] })
</script>

<template>
  <div class="extra-fields-edit">
    <div v-if="!modelValue.length" class="text-grey-7">{{ i18n.t('common.extraFields.empty') }}</div>
    <q-card
      v-for="(field, index) in modelValue"
      :key="field.id ?? `new-${index}`"
      flat
      bordered
      class="extra-fields-edit__field"
    >
      <q-card-section class="row items-center no-wrap q-py-xs q-pl-md q-pr-xs">
        <q-icon :name="fieldTypeOf(field)?.icon" color="primary" class="q-mr-sm" />
        <div class="col text-weight-medium ellipsis">{{ field.name || i18n.t('common.extraFields.field', { number: index + 1 }) }}</div>
        <template v-if="!readonly">
          <q-btn
            flat
            round
            dense
            size="sm"
            icon="mdi-arrow-up"
            :disable="index === 0"
            @click="moveField(index, -1)"
          />
          <q-btn
            flat
            round
            dense
            size="sm"
            icon="mdi-arrow-down"
            :disable="index === modelValue.length - 1"
            @click="moveField(index, 1)"
          />
          <q-btn
            flat
            round
            dense
            size="sm"
            color="negative"
            icon="mdi-minus-circle-outline"
            @click="removeField(index)"
          />
        </template>
      </q-card-section>
      <q-separator />
      <q-card-section class="row q-col-gutter-sm q-pt-sm q-pb-none">
        <q-input
          :model-value="field.name"
          :class="isMobile ? 'col-12' : 'col-6'"
          :label="i18n.t('common.name')"
          :readonly="readonly"
          lazy-rules
          :rules="[val => !!val?.trim() || i18n.t('common.required')]"
          @update:model-value="updateField(index, { name: $event })"
        />
        <q-select
          :model-value="field.field_type"
          :class="isMobile ? 'col-12' : 'col-6'"
          :label="i18n.t('common.type')"
          :options="fieldTypes"
          option-value="id"
          option-label="name"
          emit-value
          map-options
          :readonly="readonly"
          @update:model-value="onTypeChange(index, $event)"
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section avatar><q-icon :name="scope.opt.icon" /></q-item-section>
              <q-item-section>{{ scope.opt.name }}</q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-select
          v-if="field.field_type === extraFieldTypeEnum.SELECT"
          :model-value="field.options"
          class="col-12"
          :label="i18n.t('common.option', 2)"
          :hint="i18n.t('common.extraFields.optionsHint')"
          multiple
          use-chips
          use-input
          hide-dropdown-icon
          new-value-mode="add-unique"
          :readonly="readonly"
          lazy-rules
          :rules="[val => val.length > 0 || i18n.t('common.required')]"
          @update:model-value="updateField(index, { options: $event })"
        />
      </q-card-section>
    </q-card>
    <div v-if="!readonly">
      <q-btn
        flat
        no-caps
        color="primary"
        icon="mdi-plus-circle-outline"
        :label="i18n.t('common.extraFields.add')"
        @click="update([...modelValue, newExtraField()])"
      />
    </div>
  </div>
</template>

<style scoped>
.extra-fields-edit {
  display: flex;
  flex-direction: column;
  gap: .75rem;
}
</style>
